// Internal cache for generated colors to avoid recalculating on every render
const colorCache = new Map();

/**
 * Generate a color suitable for borders/cards with good contrast
 *
 * @param {string} input - The input string to generate a color from
 * @returns {string} - Hex color string
 */
export function generateUniqueColor(input) {
  if (!input) return '#E0E0E0';

  // Check cache first
  if (colorCache.has(input)) {
    return colorCache.get(input);
  }

  const base = hashStringToInt(input);
  // Generate hue from a mixed hash for better dispersion
  const hue = mixHash32(base) % 360;
  // Vary saturation/lightness per input to avoid near-duplicates
  const saturation = 70 + (mixHash32(base ^ 0x9e3779b9) % 21); // 70-90
  const lightness = 46 + (mixHash32(base ^ 0x85ebca6b) % 9); // 46-54
  const color = hslToHex(hue, saturation, lightness);

  // Store in cache
  colorCache.set(input, color);

  return color;
}

/**
 * Generate a color suitable for backgrounds with good contrast for text
 *
 * @param {string} input - The input string to generate a color from
 * @returns {string} - Hex color string
 */
export function generateBackgroundColor(input) {
  if (!input) return '#F5F5F5';

  // Check cache first
  const cacheKey = `bg_${input}`;
  if (colorCache.has(cacheKey)) {
    return colorCache.get(cacheKey);
  }

  // Create a hash from the input string
  const base = hashStringToInt(input);
  // Use mixed channels for better separation across inputs
  const r = mixHash32(base) & 0xff;
  const g = mixHash32(base ^ 0x9e3779b9) & 0xff;
  const b = mixHash32(base ^ 0x85ebca6b) & 0xff;

  // Ensure the color is light enough for dark text
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  let color;

  if (brightness < 200) {
    // If too dark, lighten it
    color = `#${Math.min(255, Math.floor(r * 1.4))
      .toString(16)
      .padStart(2, '0')}${Math.min(255, Math.floor(g * 1.4))
      .toString(16)
      .padStart(2, '0')}${Math.min(255, Math.floor(b * 1.4))
      .toString(16)
      .padStart(2, '0')}`;
  } else {
    color = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b
      .toString(16)
      .padStart(2, '0')}`;
  }

  // Store in cache
  colorCache.set(cacheKey, color);

  return color;
}

/**
 * Clear the color cache (useful for testing or memory management)
 */
export function clearColorCache() {
  colorCache.clear();
}

/**
 * Get the current size of the color cache
 */
export function getColorCacheSize() {
  return colorCache.size;
}

/**
 * Create a deterministic positive integer hash from a string
 *
 * @param {string} str - Input string to hash
 * @returns {number} - Non-negative 32-bit integer hash
 */
export function hashStringToInt(str) {
  if (str == null) return 0;
  let hash = 0;
  const value = str.toString();
  for (let i = 0; i < value.length; i++) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0; // force 32-bit integer
  }
  return Math.abs(hash);
}

/**
 * Convert HSL color to HEX string
 *
 * @param {number} h - Hue (0-360)
 * @param {number} s - Saturation (0-100)
 * @param {number} l - Lightness (0-100)
 * @returns {string} - Hex color string, e.g. #aabbcc
 */
export function hslToHex(h, s, l) {
  s /= 100;
  l /= 100;
  const k = n => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = n => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  const toHex = x =>
    Math.round(255 * x)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`;
}

/**
 * Mix a 32-bit integer to achieve avalanche effect (MurmurHash3 fmix32).
 * Ensures small input changes produce large distribution changes.
 */
function mixHash32(h) {
  h |= 0;
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return h >>> 0;
}
